import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { BulkUrlType } from "../../../shared/types/bulk-upload/bulk-url.type";
import { Country, Location } from "./location.type";

@Injectable({
  providedIn: "root",
})
export class LocationService {
  basePath = `${environment.serverUrl}/locations`;
  countryBasePath = `${environment.serverUrl}/countries`;
  basePathCsvQueue = `${this.basePath}/queue-csv`;

  constructor(private http: HttpClient) {}

  createLocation(data: Location): Observable<Location> {
    return this.http.post<Location>(this.basePath, data).pipe(
      tap((_data: Location) => console.log()),
      catchError(this.handleError<any>("getLocations")),
    );
  }

  getLocations(options?: QueryParams<Location>): Observable<PaginatedResponse<Location>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<Location>>(this.basePath, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("getLocations")),
    );
  }

  getLocation(locationId: string, options?: QueryParams<Location>): Observable<Location> {
    let params: HttpParams = new HttpParams();
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<Location>(`${this.basePath}/${locationId}`, { params }).pipe(
      tap((_data: Location) => console.log()),
      catchError(this.handleError<any>("getLocation")),
    );
  }

  updateLocation(locationId: string, data): Observable<Location> {
    return this.http.put<Location>(`${this.basePath}/${locationId}`, data).pipe(
      tap((_data: Location) => console.log()),
      catchError(this.handleError<any>("updateLocation")),
    );
  }

  linkUsers(locationId: string, body): Observable<void> {
    return this.http.put<void>(`${this.basePath}/${locationId}/users`, body).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("linkUsers")),
    );
  }

  getPutSignedUrl(): Observable<BulkUrlType> {
    return this.http.get<any>(`${this.basePath}/batchs/bulk-upload-url`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>()),
    );
  }

  uploadBatch(url: string, csvFile: File) {
    return this.http.put<any>(url, csvFile);
  }

  getCountries(options?: QueryParams<Country>): Observable<PaginatedResponse<Country>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<Country>>(this.countryBasePath, { params }).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getLocationsQueue(options?: QueryParams<Location>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>()),
    );
  }
  private handleError<T>(operation = "operation", result?: T) {
    return (error: unknown): Observable<T> => {
      console.error(`${error} ${operation}`);
      return throwError(error || "Something went wrong");
    };
  }
}
